/**
 * FormDataに変換可能な値の型
 *
 * form-factory の FieldValueType と互換性を持つが、
 * lib/api 層は features に依存しないため独自に定義
 */
type FormDataValue =
  | File
  | File[]
  | { s3Url: string; [key: string]: unknown }
  | { s3Url: string; [key: string]: unknown }[]
  | { value: string; label: string; [key: string]: unknown }
  | string
  | number
  | boolean
  | string[]
  | null
  | undefined;

/**
 * フォームの値をFormDataに変換するシリアライザー
 *
 * 以下の値タイプに対応:
 * - File: ファイル名をエンコードして追加
 * - File[]: 複数ファイルを同じキーで追加
 * - s3Urlを持つオブジェクト: 既にアップロード済みのファイル参照（JSON文字列化）
 * - 配列: マルチセレクトの値を同じキーで複数追加
 * - null/undefined: スキップ
 * - その他: 文字列に変換
 *
 * @example
 * ```ts
 * const res = await apiClient.PUT("/v2/...", {
 *   body,
 *   bodySerializer: createFormDataSerializer,
 * });
 * ```
 */
export function createFormDataSerializer(
  body: Record<string, FormDataValue> | undefined,
): FormData {
  const formData = new FormData();

  if (!body) {
    return formData;
  }

  for (const [key, value] of Object.entries(body)) {
    // File[] または 既にアップロード済みのファイル参照の配列
    if (
      Array.isArray(value) &&
      value.every(
        (item) =>
          item instanceof File ||
          (typeof item === 'object' && item !== null && 's3Url' in item),
      )
    ) {
      for (const file of value) {
        if (file instanceof File) {
          formData.append(key, file, encodeURIComponent(file.name));
        } else {
          // 既にアップロード済みのファイル参照
          formData.append(key, JSON.stringify(file));
        }
      }
      continue;
    }

    // 単一のFile
    if (value instanceof File) {
      formData.append(key, value, encodeURIComponent(value.name));
      continue;
    }

    // 既にアップロード済みのファイル参照（単一）
    if (typeof value === 'object' && value !== null && 's3Url' in value) {
      formData.append(key, JSON.stringify(value));
      continue;
    }

    // 配列（マルチセレクト等）
    if (Array.isArray(value)) {
      for (const item of value) {
        formData.append(key, String(item));
      }
      continue;
    }

    // undefined/null はスキップ（空文字列は送信する）
    if (value === undefined || value === null) {
      continue;
    }

    // {value, label} オブジェクト（バックエンドの resolveFieldLabels が付与）から値を抽出
    if (
      typeof value === 'object' &&
      value !== null &&
      'value' in value &&
      'label' in value &&
      typeof (value as Record<string, unknown>).value === 'string' &&
      typeof (value as Record<string, unknown>).label === 'string'
    ) {
      formData.append(key, value.value);
      continue;
    }

    // その他の値は文字列に変換
    formData.append(key, String(value));
  }

  return formData;
}
